import React, { useState, useEffect } from "react";
import Slider from "react-slick";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./homescreen.css";

// Import flyer images
import mainFlyer from "../../assets/images/flyers/mainFlyer.jpeg";
import nexus from "../../assets/images/flyers/Nexus.jpeg";
import sharkTank from "../../assets/images/flyers/shark tank flyer.jpg";

const HomeScreen = () => {
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.token);
  
  // Dynamic window width and player height calculation
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [playerHeight, setPlayerHeight] = useState(
    Math.min(window.innerWidth, 1200) * (9 / 16)
  );

  // Image modal state
  const [selectedImage, setSelectedImage] = useState(null);
  const [zoom, setZoom] = useState(1);

  // Flyer images data
  const flyerImages = [
    { image: mainFlyer, title: "Conference Information" },
    { image: nexus, title: "Shalamar Nexus" },
    { image: sharkTank, title: "Shark Tank Competition" },
  ];

  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      setWindowWidth(newWidth);
      const containerWidth = Math.min(newWidth, 1200);
      setPlayerHeight(containerWidth * (9 / 16));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Image modal functions
  const openModal = (image) => {
    setSelectedImage(image);
    setZoom(1);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const zoomIn = () => {
    setZoom((prevZoom) => prevZoom + 0.2);
  };

  const zoomOut = () => {
    setZoom((prevZoom) => (prevZoom > 1 ? prevZoom - 0.2 : 1));
  };

  // Doctor slider settings
  const doctorSliderSettings = {
    dots: false,
    arrows: false,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2000,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 3 } },
      { breakpoint: 600, settings: { slidesToShow: 2 } },
      { breakpoint: 480, settings: { slidesToShow: 1 } },
    ],
  };

  // State for latest video from SIHS API
  const [latestVideoId, setLatestVideoId] = useState(null);
  const [videoTitle, setVideoTitle] = useState("");
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(null);

  const CHANNEL_ID = "UCvq31rUs-ZBI97KgjUrEZ3A";

  useEffect(() => {
    const fetchLatestVideo = async () => {
      try {
        setVideoLoading(true);
        setVideoError(null);

        const response = await fetch(
          `https://apis.sihs.org.pk/latest/${CHANNEL_ID}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("Latest Video API Response:", data);

        setLatestVideoId(data.VideoId);
        setVideoTitle(data.Title || "Latest Video");
      } catch (err) {
        console.error("Error fetching latest video:", err);
        setVideoError(err.message);
        setLatestVideoId(null);
      } finally {
        setVideoLoading(false);
      }
    };

    fetchLatestVideo();
  }, [CHANNEL_ID]);

  const getEmbedUrl = (videoId) =>
    `https://www.youtube.com/embed/${videoId}?rel=0`;

  // State for doctors slider data
  const [doctors, setDoctors] = useState([]);
  const [docLoading, setDocLoading] = useState(true);
  const [docError, setDocError] = useState(null);

  useEffect(() => {
    fetch("https://i2.sihs.org.pk/speakers/doctors.json")
      .then((response) => response.json())
      .then((data) => {
        setDoctors(data);
        setDocLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching doctors data:", error);
        setDocError(error.message);
        setDocLoading(false);
      });
  }, []);

  return (
    <div className="home-screen-container">
      <div className="home-screen">
        <div className="home-data-container">
          {!token && (
            <button 
              className="signup-top-button" 
              onClick={() => navigate("/signup")}
            >
              Sign Up
            </button>
          )}
          <h1 className="mobile-date">20-April-2025 to 25-April-2026</h1>
          <div className="main-data">
            {/* Image Cards */}
            {flyerImages.map((flyer, index) => (
              <div key={index} className="data-lists image-card">
                <div
                  className="image-container"
                  onClick={() => openModal(flyer.image)}
                >
                  <img
                    src={flyer.image}
                    alt={flyer.title}
                    className="flyer-image"
                  />
                  <div className="image-overlay">
                    <span>Click to enlarge</span>
                  </div>
                </div>
                <h3 className="image-title">{flyer.title}</h3>
              </div>
            ))}
          </div>
        </div>

        {/* Latest Video Section */}
        <div className="latest-video-section">
          <h2 className="video-section-title">Latest Video</h2>
          {videoLoading ? (
            <div className="video-loading">
              <div className="loading-spinner"></div>
              <p>Loading latest video...</p>
            </div>
          ) : videoError ? (
            <div className="video-error">
              <p>Unable to load video. Please try again later.</p>
            </div>
          ) : latestVideoId ? (
            <div className="video-container">
              <div className="video-wrapper">
                <iframe
                  src={getEmbedUrl(latestVideoId)}
                  title={videoTitle}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="video-iframe"
                ></iframe>
              </div>
              {videoTitle && <p className="video-title">{videoTitle}</p>}
            </div>
          ) : (
            <div className="video-error">
              <p>No video available at the moment.</p>
            </div>
          )}
        </div>

        {/* Slider Section for Doctor Images */}
        <div className="slider-container">
          <h2>Our Esteemed Speakers</h2>
          {docLoading ? (
            <p>Loading doctors...</p>
          ) : docError ? (
            <p>Error: {docError}</p>
          ) : (
            <Slider {...doctorSliderSettings}>
              {doctors.map((doctor, index) => (
                <div key={index} className="doctor-slide">
                  <img
                    src={doctor.image}
                    alt={doctor.name}
                    className="doctor-img"
                  />
                  <p className="doctor-name">{doctor.name}</p>
                  <p className="doctor-designation">
                    {doctor.designation || doctor.title}
                  </p>
                </div>
              ))}
            </Slider>
          )}
          <div className="view-speakers-link">
            <Link to="/speakers" onClick={() => window.scrollTo(0, 0)}>
              View All Speakers
            </Link>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              ×
            </button>
            <div className="modal-image-container">
              <img
                src={selectedImage}
                alt="Enlarged"
                style={{ transform: `scale(${zoom})` }}
                className="modal-image"
              />
            </div>
            <div className="zoom-controls">
              <button onClick={zoomOut}>Zoom Out</button>
              <button onClick={zoomIn}>Zoom In</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeScreen;
